'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { trackEvent } from '@/lib/analytics';

export default function HomeCTA() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const els = ref.current?.querySelectorAll('[data-cta-reveal]');
          els?.forEach((el, i) => {
            const element = el as HTMLElement;
            setTimeout(() => {
              element.style.opacity = '1';
              element.style.transform = 'translateY(0)';
              element.style.filter = 'blur(0)';
            }, i * 100);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="cta"
      className="py-28 px-6 relative z-10 overflow-hidden"
      style={{ background: 'var(--color-surface)' }}
      aria-label="Call to action for EMD application">

      
      <div className="max-w-7xl mx-auto">
        <div
          className="rounded-3xl overflow-hidden relative"
          style={{ background: 'var(--color-primary)' }}>
          
          {/* Background image */}
          <div className="absolute inset-0 z-0">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1e716e7d6-1775330016792.png"
              alt="Commercial real estate deal closing paperwork on dark desk, low-key professional lighting, deep shadows, moody atmosphere"
              fill
              className="object-cover opacity-20"
              sizes="100vw" />
            
          </div>
          <div
            className="absolute inset-0 z-10"
            style={{
              background:
              'linear-gradient(135deg, rgba(13,27,23,0.95) 0%, rgba(27,67,50,0.85) 100%)'
            }} />
          

          {/* Decorative dot pattern */}
          <div
            className="absolute inset-0 z-10 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)',
              backgroundSize: '24px 24px'
            }} />
          

          <div className="relative z-20 grid grid-cols-1 lg:grid-cols-2 gap-12 p-12 md:p-16 lg:p-20">
            {/* Left */}
            <div>
              <div
                data-cta-reveal
                style={{
                  opacity: 0,
                  transform: 'translateY(24px)',
                  filter: 'blur(4px)',
                  transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
                }}>
                
                <p className="section-label mb-4" style={{ color: '#74C69D' }}>
                  Ready to Close Faster?
                </p>
                <h2
                  id="cta-heading"
                  className="font-display mb-6"
                  style={{
                    fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                    color: 'white',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.05
                  }}>
                  
                  Your next deal doesn&apos;t have to wait for capital to free up.
                </h2>
                <p className="text-base leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.6)' }}>Submit your deal details and our team will get back to you with a funding decision — typically within 24-48 hours.


                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    href="/contact"
                    className="btn-accent text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full"
                    onClick={() => trackEvent('button_click', { button_id: 'cta_submit_deal', location: 'home_cta' })}>
                    
                    Submit a Deal
                  </Link>
                  <Link
                    href="/emd-program"
                    className="text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-full"
                    onClick={() => trackEvent('button_click', { button_id: 'cta_emd_program', location: 'home_cta' })}
                    style={{
                      border: '1.5px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                    
                    See the EMD Program
                  </Link>
                  <Link
                    href="/pricing"
                    className="text-sm font-medium underline underline-offset-4"
                    onClick={() => trackEvent('button_click', { button_id: 'cta_view_pricing', location: 'home_cta' })}
                    style={{ color: '#74C69D' }}>
                    View Pricing →
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Quick trust signals */}
            <div className="flex flex-col justify-center gap-5">
              {[
              {
                icon: '⚡',
                title: 'Fast & Reliable Funding',
                body: 'From completed application to wire in escrow.'
              },
              {
                icon: '📋',
                title: '1-Page Application',
                body: 'No tax returns, no personal financial statements.'
              },
              {
                icon: '🤝',
                title: 'Deal-by-Deal Basis',
                body: 'No long-term commitments or subscription fees.'
              },
              {
                icon: '🌐',
                title: 'Any Industry, Any Deal',
                body: 'Real estate, M&A, aviation, government contracts, oil & gas, maritime, and more.'
              }].
              map((item, i) =>
              <div
                key={item.title}
                data-cta-reveal
                className="flex items-start gap-4"
                style={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  filter: 'blur(4px)',
                  transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
                }}>
                
                  <span className="text-xl mt-0.5">{item.icon}</span>
                  <div>
                    <p
                    className="text-sm font-semibold mb-0.5"
                    style={{ color: 'white' }}>
                    
                      {item.title}
                    </p>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      {item.body}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}